import { useEffect, useState } from 'react';
import { espaceSalarieApi, ProfilEmploye } from '../api/espaceSalarieApi';

const f = (n: number | string) => Number(n).toLocaleString('fr-FR') + ' F';

export function MonProfilPage() {
  const [profil, setProfil] = useState<ProfilEmploye | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    espaceSalarieApi.obtenirMonProfil()
      .then(data => {
        setProfil(data);
        setErreur(null);
      })
      .catch(() => setErreur('Impossible de charger votre profil.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto animate-pulse space-y-4">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (erreur || !profil) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-red-700 font-semibold">{erreur ?? 'Profil introuvable'}</p>
        </div>
      </div>
    );
  }

  const initiales = `${profil.nom?.[0] ?? ''}${profil.prenoms?.[0] ?? ''}`.toUpperCase();

  const infos = [
    { label: 'Nom', value: `${profil.nom} ${profil.prenoms}` },
    { label: 'Fonction', value: profil.fonction },
    { label: 'N° CNSS', value: profil.numeroCnss ?? '—' },
    { label: 'IFU', value: profil.ifu ?? '—' },
    { label: 'NPI', value: profil.npi ?? '—' },
    { label: 'Lieu de travail', value: profil.lieuTravail ?? '—' },
    { label: 'Téléphone', value: profil.telephone ?? '—' },
    { label: 'Matricule', value: profil.numeroMatricule ?? '—' },
    {
      label: "Date d'embauche",
      value: new Date(profil.dateEmbauche).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
      }),
    },
    { label: 'Salaire de base', value: f(profil.salaireBase) },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Mon profil</h1>

      <div className="bg-surface border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* En-tête avatar */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-8 flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {initiales}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{profil.nom} {profil.prenoms}</h2>
            <p className="text-emerald-100">{profil.fonction}</p>
            <span className={`mt-2 inline-block px-3 py-0.5 rounded-full text-xs font-semibold ${
              profil.actif ? 'bg-white/20 text-white' : 'bg-red-400/20 text-red-100'
            }`}>
              {profil.actif ? '● Actif' : '○ Inactif'}
            </span>
          </div>
        </div>

        {/* Informations */}
        <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
          {infos.map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{label}</p>
              <p className="text-sm font-medium text-slate-800">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MonProfilPage;
